import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBrand } from "@/config/brands";
import { Shield } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = () => {
  const brand = useBrand();
  const { t } = useTranslation('legal');

  return (
    <div className="min-h-screen bg-background pb-24">
      <Helmet>
        <title>{t('privacy.meta.pageTitle', { brandName: brand.name })}</title>
        <meta name="description" content={t('privacy.meta.description', { brandName: brand.name })} />
        <link rel="canonical" href={`${brand.primaryDomain}/privacy`} />
        <meta property="og:title" content={t('privacy.meta.pageTitle', { brandName: brand.name })} />
        <meta property="og:description" content={t('privacy.meta.description', { brandName: brand.name })} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/privacy`} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={t('privacy.meta.pageTitle', { brandName: brand.name })} />
        <meta name="twitter:description" content={t('privacy.meta.description', { brandName: brand.name })} />
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-br from-matrix-green/10 to-blood-red/10 border-b border-matrix-green/20">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-matrix-green" />
            <h1 className="text-3xl font-bold text-foreground">{t('privacy.title')}</h1>
          </div>
          <p className="text-muted-foreground">{t('common.lastUpdated', { date: 'January 2025' })}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.introduction')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              {brand.name} {brand.legal.organization && brand.legal.organization !== brand.name && `("${brand.legal.organization}")`} ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
            </p>
            <p>
              Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy,
              please do not access the site.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.informationCollected')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">
              We may collect information about you in a variety of ways:
            </p>

            <div className="space-y-3">
              <div>
                <p className="font-semibold text-foreground">Personal Data</p>
                <p>
                  If you create an account or contact us, we may collect personally identifiable information, such as:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>User preferences and settings</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-foreground">Usage Data</p>
                <p>
                  We automatically collect certain information when you visit, use, or navigate the site:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Device information (type, operating system)</li>
                  <li>Referring website addresses</li>
                  <li>Search terms used to find our site</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-foreground">Cookies and Tracking Technologies</p>
                <p>
                  We may use cookies, web beacons, tracking pixels, and other tracking technologies to collect and store
                  information about your interactions with our site. You can control cookies through your browser settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.howWeUse')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              We use the information we collect or receive to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide, operate, and maintain our website and services</li>
              <li>Improve, personalize, and expand our website and services</li>
              <li>Understand and analyze how you use our website</li>
              <li>Develop new products, services, features, and functionality</li>
              <li>Communicate with you for customer service, updates, and promotional purposes</li>
              <li>Send you marketing and promotional communications (with your consent)</li>
              <li>Find and prevent fraud and security issues</li>
              <li>Comply with legal obligations</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.advertisingAnalytics')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground">
              Third-Party Advertising Partners
            </p>
            <p>
              We may use third-party advertising companies to serve ads when you visit our website. These companies may
              use information about your visits to this and other websites to provide advertisements about goods and
              services of interest to you.
            </p>
            <p>
              <strong>Google AdSense:</strong> We use Google AdSense to display advertisements on our site. Google may use
              cookies and other tracking technologies to serve ads based on your prior visits to our website or other websites.
              You can opt out of personalized advertising by visiting{" "}
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-matrix-green hover:underline">
                Google's Ads Settings
              </a>.
            </p>
            <p className="font-semibold text-foreground">
              Analytics Services
            </p>
            <p>
              We may use third-party analytics services (such as Google Analytics) to monitor and analyze the use of our
              service. These services help us understand user behavior and improve our platform.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.disclosure')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              We may share information we have collected about you in certain situations:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>By Law or to Protect Rights:</strong> If we believe disclosure is necessary to comply with applicable law, regulation, legal process, or governmental request</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business</li>
              <li><strong>Third-Party Service Providers:</strong> We may share your information with third parties that perform services for us or on our behalf (hosting, analytics, advertising)</li>
              <li><strong>With Your Consent:</strong> We may disclose your information for any other purpose with your consent</li>
            </ul>
            <p className="font-semibold text-foreground mt-4">
              We do NOT sell your personal information to third parties.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.dataRetention')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              We will retain your information only for as long as necessary to fulfill the purposes outlined in this
              Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need
              your information, we will delete or anonymize it.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.yourRights')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete personal information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-Out:</strong> Opt out of marketing communications at any time</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where we rely on your consent</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at:{" "}
              <span className="font-mono text-matrix-green">{brand.contact.support}</span>
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.cookiePreferences')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              You can manage your cookie preferences and advertising consent through your browser settings or through
              our cookie consent banner. You can also visit your browser's settings to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Delete all cookies</li>
              <li>Block all cookies</li>
              <li>Allow all cookies</li>
              <li>Block third-party cookies</li>
              <li>Clear all cookies when you close your browser</li>
            </ul>
            <p className="mt-4">
              Note that blocking cookies may impact your experience on our site and limit certain features.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.dataSecurity')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              We use administrative, technical, and physical security measures to help protect your personal information.
              While we have taken reasonable steps to secure the personal information you provide to us, please be aware
              that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission
              can be guaranteed against any interception or other type of misuse.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.childrenPrivacy')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              Our website is not intended for children under 13 years of age. We do not knowingly collect personal
              information from children under 13. If you are a parent or guardian and believe your child has provided
              us with personal information, please contact us.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.changes')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new
              Privacy Policy on this page and updating the "Last updated" date at the top of this Privacy Policy.
            </p>
            <p>
              You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy
              are effective when they are posted on this page.
            </p>
          </CardContent>
        </Card>

        <Card className="border-matrix-green/20">
          <CardHeader>
            <CardTitle>{t('privacy.sections.contact')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
            <p>
              If you have questions or comments about this Privacy Policy, please contact us at:
            </p>
            <p className="font-mono text-matrix-green text-base">
              {brand.contact.support}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
